import { Component, inject, signal, computed, input, effect, Input } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';

// Kendo Components
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { DatePickerModule } from '@progress/kendo-angular-dateinputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { MultiSelectModule } from '@progress/kendo-angular-dropdowns';
import { SVGIcon, filterIcon, chevronDownIcon, chevronUpIcon } from '@progress/kendo-svg-icons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { TooltipModule } from '@progress/kendo-angular-tooltip';

// Core Components
import { PopupHostComponent } from 'src/app/core/components/popup-host.component';

// Services
import {
  ReportingService,
  ReportComplianceFrameworks,
  ReportComplianceTags,
  ReportTypes,
  ReportCategories,
  Customer
} from 'src/app/core/services/reporting/reporting.service';
import { ReportJobService, ReportJobConfig, ReportSchedule } from 'src/app/core/services/reporting/report-job.service';
import { ScheduledReportService, Recipient } from  'src/app/core/services/reporting/scheduled-report.service';
import { OrganizationService } from 'src/app/features/admin/organizations/services/organization.service';

// Models
import { ReportTemplate } from './reporting-generate-mock-data/report-templates-mock-data';

// Popup Components
import { RequestCompliancePopupComponent } from './components/request-compliance-popup.component';
import { ComplianceTabLauncherComponent } from './components/compliance-tab-launcher.component';

import { NotificationService } from '@progress/kendo-angular-notification';

/**
 * Generate Component
 * Multi-customer report generation interface
 */
@Component({
  selector: 'app-reporting-generate',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DropDownListModule,
    DatePickerModule,
    ButtonsModule,
    MultiSelectModule,
    IconsModule,
    TooltipModule,
    ComplianceTabLauncherComponent
  ],
  templateUrl: './generate.component.html',
  styleUrls: ['./generate.component.scss'],
})
export class GenerateComponent extends PopupHostComponent {
  private fb = inject(FormBuilder);
  private notificationService = inject(NotificationService);
  private reportingService = inject(ReportingService);
  private reportJobService = inject(ReportJobService);
  private reportScheduleService = inject(ScheduledReportService);
  private organizationService = inject(OrganizationService);

  public organizationId = input<number | null>(null);
  @Input() userId: number | null = null;

  // Input from parent: User's primary organization type
  public userPrimaryOrgType = input<number | null>(null);

  // Input from parent: User's primary organization id
  public userPrimaryOrgId = input<number | null>(null);

  // Selected organization details (fetched based on organizationId)
  public selectedOrgName = signal<string>('My Organization');
  public selectedOrgType = signal<number | null>(null);

  // Kendo SVG Icons
  public filterIcon: SVGIcon = filterIcon;
  public chevronDownIcon: SVGIcon = chevronDownIcon;
  public chevronUpIcon: SVGIcon = chevronUpIcon;

  // Form
  public generateForm!: FormGroup;

  // Data
  public customers = signal<Customer[]>([]);
  public templates = signal<ReportTemplate[]>([]);
  public isLoading = signal<boolean>(true);
  public isGenerating = signal<boolean>(false);

  // Aggregate report
  public aggregateReport = signal<boolean>(false);

  // Filter state
  public searchQuery = signal<string>('');
  public selectedCategoryFilter = signal<number>(0); // 0 = All
  public selectedComplianceFilters = signal<number[]>([]);
  public selectedFrameworkFilter = signal<number | string>('all');
  public showFilters = signal<boolean>(false);
  public selectedReportTypeId = signal<number | null>(null);

  // Mock data
  public reportTypes = signal<ReportTypes[]>([]);
  public complianceTags = signal<ReportComplianceTags[]>([]);
  public generalComplianceTags = signal<ReportComplianceTags[]>([]);
  public categories: ReportCategories[] = [];
  public complianceFrameworks: ReportComplianceFrameworks[] = [];
  public allRecipients = signal<Recipient[]>([]);
  public filteredRecipients = signal<Recipient[]>([]);

  // Date range quick options
  public dateRangeOptions = [
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'Last Quarter', days: 90 },
  ];

  // Selected date range (tracks which option is active)
  public selectedDateRange = signal<number | 'custom'>(30);
  private firstLoad = true;

  constructor() {
    super();
    this.initializeForm();
    this.initEffect();
    this.loadAllComplianceTags();
  }

  private initEffect(): void {
    effect(() => {
      const orgId = this.organizationId();
      this.resetStateForOrganization();

      if (orgId !== null) {
        if (!this.firstLoad) {
          this.initializeForm();
        }

        this.loadOrganizationDetails(orgId);
        this.loadData();
        this.firstLoad = false;
      }
    }, { allowSignalWrites: true });
  }

  /**
   * Fetch organization details (name, type) based on organizationId
   * This ensures the "My Organization" display stays in sync with navbar selection
   */
  private loadOrganizationDetails(orgId: number): void {
    this.organizationService.getOrganizationById(orgId).subscribe({
      next: (org) => {
        this.selectedOrgName.set(org.name ?? 'My Organization');
        this.selectedOrgType.set(org.account_type ?? null);
      },
      error: (error) => {
        console.error('Error loading organization details:', error);
      }
    });
  }


  private resetStateForOrganization(): void {
    // Loading state
    this.isLoading.set(true);
    this.isGenerating.set(false);

    // Clear reactive state that depends on the organization
    this.customers.set([]);
    this.templates.set([]);
    this.selectedReportTypeId.set(null);
    this.aggregateReport.set(false);
  }

  // Computed values
  public selectedReportType = computed(() => {
    const reportTypeId = this.selectedReportTypeId();
    if (!reportTypeId) return undefined;
    return this.reportTypes().find((t) => t.id === reportTypeId);
  });

  public selectedReportFormat = computed(() => {
    return this.selectedReportType()?.format || 'PDF';
  });

  public isCSVReport = computed(() => {
    return this.selectedReportFormat().toUpperCase().includes('CSV');
  });

  public showAggregateCheckbox(): boolean {
    return this.isCSVReport() && this.getTotalSelectedCount() > 1;
  }

  // Computed: Selected organization type label for display
  public selectedOrgTypeLabel = computed(() => {
    const type = this.selectedOrgType();
    if (type === 2) return 'MSP';
    if (type === 3) return 'MMSP';
    return '';
  });

  public selectedReportName = computed(() => {
    return this.selectedReportType()?.name || '';
  });

  // Filtered report types based on search, framework, and compliance tags
  // Note: Category filtering is done server-side via getReportTypes(categoryId)
  public filteredReportTypes = computed(() => {

    let filtered = [...this.reportTypes()];

    // Filter by search query
    const query = this.searchQuery().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (report) => report.name.toLowerCase().includes(query) || report.description.toLowerCase().includes(query)
      );
    }

    // Filter by compliance framework
    const framework = this.selectedFrameworkFilter();
    if (framework && framework !== 'all') {
      filtered = filtered.filter((report) => {
        // Check if report has any tags from the selected framework
        return report.reportTemplate.complianceTags.some((tagObj) => {
          const tag = this.complianceTags().find((t) => t.id === tagObj.id);
          return tag?.category_id === framework;
        });
      });
    }

    // Filter by specific compliance tags
    const complianceFilters = this.selectedComplianceFilters();
    if (complianceFilters.length > 0) {
      filtered = filtered.filter((report) =>
        complianceFilters.some((tagId) =>
          report.reportTemplate.complianceTags.some((tag) => tag.id === tagId)
        )
      );
    }
    return filtered;
  });

  // Initialize the form
  private initializeForm(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.selectedReportTypeId.set(null);
    this.selectedDateRange.set(30);
    this.aggregateReport.set(false);

    this.generateForm = this.fb.group({
      reportType: [null, Validators.required],
      includeSelfOrganization: [false],
      selectedCustomers: [[]],
      dateRangeStart: [thirtyDaysAgo, Validators.required],
      dateRangeEnd: [today, Validators.required],
      includeExecutiveSummary: [true],
      includeDetailedFindings: [true],
      includeRemediation: [false],
      deliveryMethod: ['download', Validators.required],
      emailRecipients: [[]],
      storageDestination: [''],
      scheduleFrequency: ['weekly'],
      scheduleRecipients: [[] as Recipient[]],
      scheduleTime: [new Date(0, 0, 0, 2, 0, 0), Validators.required]
    });

    merge(
      this.generateForm.get('selectedCustomers')!.valueChanges,
      this.generateForm.get('includeSelfOrganization')!.valueChanges
    ).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (this.getTotalSelectedCount() <= 1) {
        this.aggregateReport.set(false);
      }
    });
  }

  // Load customers and templates
  private loadData(): void {
    const orgId = this.organizationId();

    if (orgId !== null) {
      this.isLoading.set(true);
      this.reportingService.getActiveCustomers(orgId).subscribe({
        next: (customers) => {
          this.customers.set(customers);
          this.updateCustomerValidation();
        },
        error: (error) => {
          console.error('Error loading customers:', error);
        },
      });

      this.reportingService.getReportTemplates().subscribe({
        next: (templates) => {
          this.templates.set(templates);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading templates:', error);
          this.isLoading.set(false);
        },
      });

      this.reportingService.getReportCategories().subscribe({
        next: (data) => {
          this.categories = data;
        },
        error: (err) => console.error(err)
      });

      this.reportingService.getReportComplianceFrameworks().subscribe({
        next: (data) => {
          this.complianceFrameworks = data;
        },
        error: (err) => console.error(err)
      });

      this.reportingService.getReportTypes().subscribe({
        next: (data) => {
          this.reportTypes.set(data);
        },
        error: (err) => console.error(err)
      });

      this.reportScheduleService.getRecipients(orgId).subscribe({
          next: (data) => {
            this.allRecipients.set(data);
            this.filteredRecipients.set(data);
          },
          error: (err) => console.error(err)
        });
    }

  }

  public onRecipientFilterChange(value: string): void {
    const query = value.toLowerCase().trim();

    if (!query) {
      this.filteredRecipients.set(this.allRecipients());
      return;
    }

    this.filteredRecipients.set(
      this.allRecipients().filter(r =>
        r.name.toLowerCase().includes(query) ||
        r.email.toLowerCase().includes(query)
      )
    );
  }

  // Update customer validation based on visibility
  // For MSP/MMSP users, at least one organization must be selected (self OR children)
  private updateCustomerValidation(): void {
    const control = this.generateForm.get('selectedCustomers');
    // Clear individual field validators - we'll use form-level validation instead
    control?.clearValidators();
    control?.updateValueAndValidity();
  }

  // Check if at least one organization is selected (self OR children)
  public get hasValidOrganizationSelection(): boolean {
    // For org type 1, no customer selection needed
    // Also, if there are no child customers, the organization selection card is hidden
    // and the current organizationId will be used as fallback in getSelectedOrganizations()
    if (this.userPrimaryOrgType() === 1 || this.customers().length === 0) {
      return true;
    }

    // For MSP/MMSP with child customers, check if self is included OR children are selected
    const selfIncluded = this.generateForm?.value?.includeSelfOrganization || false;
    const childrenSelected = (this.generateForm?.value?.selectedCustomers?.length || 0) > 0;

    return selfIncluded || childrenSelected;
  }

  // Check if recipients are selected when scheduling
  public get hasValidRecipients(): boolean {
    const deliveryMethod = this.generateForm?.value?.deliveryMethod;
    // Only require recipients for schedule delivery method
    if (deliveryMethod !== 'schedule') {
      return true;
    }
    const recipients = this.generateForm?.value?.scheduleRecipients || [];
    return recipients.length > 0;
  }

  // Combined validation for the generate button
  public get canGenerate(): boolean {
    return !this.generateForm.invalid &&
           !this.isGenerating() &&
           this.hasValidOrganizationSelection &&
           this.hasValidRecipients;
  }

  // Select all customers
  public selectAllCustomers(): void {
    const allCustomerIds = this.customers().map((c) => c.id);
    this.generateForm.patchValue({ selectedCustomers: allCustomerIds });
  }

  // Clear customer selection
  public clearCustomerSelection(): void {
    this.generateForm.patchValue({ selectedCustomers: [] });
  }

  // Apply quick date range
  public applyDateRange(days: number | 'custom'): void {
    this.selectedDateRange.set(days);

    if (days !== 'custom') {
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - days);

      this.generateForm.patchValue({
        dateRangeStart: startDate,
        dateRangeEnd: today,
      });
    }
    // If 'custom', just show the date pickers without auto-setting dates
  }

  // Load a saved template
  public loadTemplate(template: ReportTemplate): void {
    this.generateForm.patchValue({
      reportType: template.report_type,
      includeExecutiveSummary: template.configuration.include_executive_summary,
      includeDetailedFindings: template.configuration.include_detailed_findings,
      includeRemediation: template.configuration.include_remediation,
      deliveryMethod: template.configuration.delivery_method,
      emailRecipients: template.configuration.email_recipients?.join(', ') || '',
      storageDestination: template.configuration.storage_destination || '',
    });
  }

    getWeeklyRange(): { startDate: Date; endDate: Date } {
      const now = new Date();

      const day = now.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;

      const startDate = new Date(now);
      startDate.setDate(now.getDate() + diffToMonday);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);

      return { startDate, endDate };
    }

    getMonthlyRange(): { startDate: Date; endDate: Date } {
      const now = new Date();

      let startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      startDate.setHours(0, 0, 0, 0);

      let endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);

      return { startDate, endDate };
    }

    getDateRange(scheduleType: 'weekly' | 'monthly'): { startDate: Date; endDate: Date } {
      return scheduleType === 'weekly'
        ? this.getWeeklyRange()
        : this.getMonthlyRange();
    }

   getNextRunAt(scheduleType: 'weekly' | 'monthly'): string {
    const now = new Date();
    let nextRunAtDate: Date;

    if (scheduleType === 'weekly') {
      const day = now.getDay();
      const diffToNextMonday = day === 0 ? 1 : 8 - day;

      nextRunAtDate = new Date(now);
      nextRunAtDate.setDate(now.getDate() + diffToNextMonday);
      nextRunAtDate.setHours(2, 0, 0, 0);
    } else {
      nextRunAtDate = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        1
      );
      nextRunAtDate.setHours(2, 0, 0, 0);
    }

    return formatDate(nextRunAtDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC');
  }


    getScheduleConfig(scheduleType: string): any {
      const time: Date = this.generateForm.get('scheduleTime')?.value;
      const timeString = time.toTimeString().split(' ')[0];

      if (scheduleType === 'weekly') {
        return {
          time: timeString,
          day_of_week: 1,
        };
      }

      return {
        time: timeString,
        day_of_month: 1,
      };
    }


  private callCreateJob(config: ReportJobConfig | ReportSchedule){
    this.reportJobService.createJob(config).subscribe({
      next: (schedule) => {
        this.notificationService.show({
          content: 'Report schedule created',
          cssClass: 'success-notification',
          animation: { type: 'fade', duration: 400 },
          position: { horizontal: 'center', vertical: 'top' },
          type: { style: 'success', icon: true },
          hideAfter: 5000,
        });
        this.isGenerating.set(false);
        this.initializeForm();
      },
      error: (err) => {
        this.notificationService.show({
          content: 'Error creating report schedule',
          cssClass: 'error-notification',
          animation: { type: 'fade', duration: 400 },
          position: { horizontal: 'center', vertical: 'top' },
          type: { style: 'error', icon: true },
          hideAfter: 7000,
        });
        this.isGenerating.set(false);
      },
    });
  }

  // Generate reports
  public generateReports(): void {
    if (!this.canGenerate) {
      return;
    }

    this.isGenerating.set(true);
    const formValue = this.generateForm.value;
    const reportType = this.reportTypes().find((t) => t.id === formValue.reportType)!;

    // Get organizations to include (handles self + children selection)
    const organizations = this.getSelectedOrganizations();

    // Ensure organizationId is a number (may be string from URL params)
    const orgId = this.organizationId();
    const primaryOrgId = typeof orgId === 'string' ? parseInt(orgId, 10) : orgId!;

    // Only use aggregate value when the checkbox is actually visible (CSV + 2+ orgs)
    const aggregateValue = this.showAggregateCheckbox() && this.aggregateReport();

    if(formValue.deliveryMethod == 'download'){
        const rawStart = formValue.dateRangeStart;
        const rawEnd = formValue.dateRangeEnd;
        const startUTC = new Date(Date.UTC(rawStart.getFullYear(), rawStart.getMonth(), rawStart.getDate(), 0, 0, 0));
        const endUTC = new Date(Date.UTC(rawEnd.getFullYear(), rawEnd.getMonth(), rawEnd.getDate(), 23, 59, 59, 999));
        const startDateParam = formatDate(startUTC, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC');
        const endDateParam = formatDate(endUTC, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC');
        const jobconfig: ReportJobConfig = {
            organizations: organizations,
            start_date: startDateParam,
            end_date: endDateParam,
            report_template_id: reportType.reportTemplate.id,
            delivery_method: 'download',
            primary_organization_id: primaryOrgId,
            report_params: { filter_type: 'filtered' },
            ...(this.isCSVReport() && { aggregate_report: aggregateValue })
        };
        this.callCreateJob(jobconfig);
    }else{
      let { startDate, endDate } = this.getDateRange(formValue.scheduleFrequency);
      const startDateParam = formatDate(startDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC');
      const endDateParam = formatDate(endDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC');
      const nextRunAt = this.getNextRunAt(formValue.scheduleFrequency);
      const scheduleConfig = this.getScheduleConfig(formValue.scheduleFrequency);
      const userPrimaryOrg = this.userPrimaryOrgId() ?? 0;

      const config: ReportSchedule = {
        name: reportType.name,
        report_template: reportType.reportTemplate.id,
        schedule_type: formValue.scheduleFrequency,
        schedule_config: scheduleConfig,
        primary_organization_id: primaryOrgId,
        params: {
          start_date: startDateParam,
          end_date: endDateParam,
          recipients: formValue.scheduleRecipients,
          organizations: organizations,
          customer_id: userPrimaryOrg,
          filter_type: 'filtered',
          ...(this.isCSVReport() && { aggregate_report: aggregateValue })
        },
        enabled: true,
        next_run_at: nextRunAt,
        created_by: this.userId!,
        delivery_method: formValue.deliveryMethod
      };
      this.callCreateJob(config);
    }

  }

  // Format date for display
  public formatDate(date: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Get customer display text
  public getCustomerText(customerId: number): string {
    const customer = this.customers().find((c) => c.id === customerId);
    return customer ? customer.name : `Customer ${customerId}`;
  }

  // Get selected customer count
  public get selectedCustomerCount(): number {
    return this.generateForm?.value?.selectedCustomers?.length || 0;
  }

  // Select a category filter and fetch report types from server
  public selectCategory(categoryId: number): void {
    this.selectedCategoryFilter.set(categoryId);
    // Fetch report types with server-side category filter
    const filterCategoryId = categoryId === 0 ? undefined : categoryId;
    this.reportingService.getReportTypes(filterCategoryId).subscribe({
      next: (data) => {
        this.reportTypes.set(data);
      },
      error: (err) => console.error(err)
    });
  }

  // Get total selected count (self + children)
  public getTotalSelectedCount(): number {
    const childCount = this.generateForm?.value?.selectedCustomers?.length || 0;
    const selfIncluded = this.generateForm?.value?.includeSelfOrganization ? 1 : 0;
    return childCount + selfIncluded;
  }

  // Build organization list for report generation
  private getSelectedOrganizations(): number[] {
    const formValue = this.generateForm.value;
    const selectedOrgs: number[] = [];

    // Add self if checkbox is checked (ensure it's a number, not string from URL params)
    if (formValue.includeSelfOrganization) {
      const orgId = this.organizationId();
      selectedOrgs.push(typeof orgId === 'string' ? parseInt(orgId, 10) : orgId!);
    }

    // Add selected child organizations
    if (formValue.selectedCustomers?.length > 0) {
      selectedOrgs.push(...formValue.selectedCustomers);
    }

    // Fallback: if nothing selected at all, default to self
    if (selectedOrgs.length === 0) {
      const orgId = this.organizationId();
      selectedOrgs.push(typeof orgId === 'string' ? parseInt(orgId, 10) : orgId!);
    }

    return selectedOrgs;
  }

  // Select a compliance framework filter
  public selectFramework(framework: number | string): void {
    this.selectedFrameworkFilter.set(framework);

    if (framework === 'all') {
      this.complianceTags.set([...this.generalComplianceTags()]);
    } else {
      const filtered = this.generalComplianceTags().filter(tag => tag.category_id === framework);
      this.complianceTags.set([...filtered]);
    }
  }

  // Toggle compliance filter
  public toggleComplianceFilter(tagId: number): void {
    const current = this.selectedComplianceFilters();
    if (current.includes(tagId)) {
      this.selectedComplianceFilters.set(current.filter((id) => id !== tagId));
    } else {
      this.selectedComplianceFilters.set([...current, tagId]);
    }
  }

  // Clear all filters
  public clearAllFilters(): void {
    this.searchQuery.set('');
    this.selectCategory(0); // Reset to 'All' and fetch all report types
    this.selectedFrameworkFilter.set('all');
    this.selectedComplianceFilters.set([]);
  }

  // Toggle filters visibility
  public toggleFilters(): void {
    this.showFilters.set(!this.showFilters());
  }

  // Check if any filters are active
  public hasActiveFilters(): boolean {
    return (
      this.selectedCategoryFilter() !== 0 ||
      this.selectedFrameworkFilter() !== 'all' ||
      this.selectedComplianceFilters().length > 0
    );
  }

  // Get count of active filters
  public activeFilterCount(): number {
    let count = 0;
    if (this.selectedCategoryFilter() !== 0) count++;
    if (this.selectedFrameworkFilter() !== 'all') count++;
    count += this.selectedComplianceFilters().length;
    return count;
  }

  // Select a report type
  public selectReportType(reportId: number): void {
    this.generateForm.patchValue({ reportType: reportId });
    this.selectedReportTypeId.set(reportId);
    this.aggregateReport.set(false);
  }

  // Get compliance tag by ID
  public getTagById(tagId: number): ReportComplianceTags | undefined {
    return this.generalComplianceTags().find((tag) => tag.id === tagId);
  }

  /**
   * Get unique compliance frameworks from a report's tags
   * Returns deduplicated list of { category, color, count, controlNames } for badge display
   */
  public getUniqueComplianceBadges(report: ReportTypes): { category: string; color: string; count: number; controlNames: string[] }[] {
    const frameworkMap = new Map<string, { color: string; count: number; controlNames: string[] }>();

    for (const tagRef of report.reportTemplate.complianceTags) {
      const tag = this.getTagById(tagRef.id);
      if (tag) {
        if (!frameworkMap.has(tag.category)) {
          frameworkMap.set(tag.category, { color: tag.color, count: 1, controlNames: [tag.name] });
        } else {
          const existing = frameworkMap.get(tag.category)!;
          existing.count++;
          existing.controlNames.push(tag.name);
        }
      }
    }

    return Array.from(frameworkMap.entries()).map(([category, data]) => ({
      category,
      color: data.color,
      count: data.count,
      controlNames: data.controlNames
    }));
  }

  // Set schedule frequency
  public setScheduleFrequency(frequency: 'weekly' | 'monthly'): void {
    this.generateForm.patchValue({ scheduleFrequency: frequency });
  }

  // Open request compliance popup
  public openRequestCompliancePopup(): void {
    const options = {
      animate: { type: 'slide', direction: 'left', duration: 200 },
      anchorAlign: { horizontal: 'right', vertical: 'top' },
      popupAlign: { horizontal: 'right', vertical: 'top' },
    };

    this.openPopup(this.bodyElement, RequestCompliancePopupComponent, options);

    setTimeout(() => {
      const popupComponent = this.popupRef?.content.instance as RequestCompliancePopupComponent;
      if (popupComponent) {
        popupComponent.close.subscribe(() => {
          this.closePopup();
        });
      }
    });
  }

  private loadAllComplianceTags(): void {
    this.reportingService.getReportComplianceTags('all').subscribe({
      next: (data) => {
        this.generalComplianceTags.set(data);
        this.complianceTags.set(data);
      },
      error: (err) => console.error(err)
    });
  }
}
